import { AvatarDropdown, AvatarName } from "@/components";
import MarkdownRenderer from "@/components/MarkdownRenderer";
import { getUserInfo } from "@/services/auth";
import { getLatestUnreadChangelog, markChangelogRead } from "@/services/system/changelog";
import { getAuthToken } from "@/utils/authStorage";
import * as AntIcons from "@ant-design/icons";
import { type Settings as LayoutSettings, SettingDrawer } from "@ant-design/pro-components";
import "@ant-design/v5-patch-for-react-19";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history } from "@umijs/max";
import { Button, Modal, Space, Tag, Typography, message } from "antd";
import dayjs from "dayjs";
import React, { Suspense, useEffect, useRef, useState } from "react";
import defaultSettings from "../config/defaultSettings";
import { errorConfig } from "./requestErrorConfig";

const isDev = process.env.NODE_ENV === "development" || process.env.CI;
const loginPath = "/user/login";
let appVersion = "-";
const LAZY_CACHE: Record<string, React.LazyExoticComponent<any>> = {};
let permissionMenus: API.RouteMenuItem[] = [];
const HIDDEN_MENU_PATHS = new Set(["/ads/create"]);

const { Title, Text } = Typography;

function getPageComponent(component: string): React.LazyExoticComponent<any> {
  if (LAZY_CACHE[component]) return LAZY_CACHE[component];
  LAZY_CACHE[component] = React.lazy(() => import(`./pages${component.slice(1)}`));
  return LAZY_CACHE[component];
}

function getMenuIcon(iconName?: string): React.ReactNode {
  if (!iconName) return undefined;
  const IconComp = (AntIcons as any)[iconName];
  return IconComp ? React.createElement(IconComp) : undefined;
}

/** 将路由菜单树转换为 ProLayout MenuDataItem 格式 */
function permissionsToMenuData(perms: API.RouteMenuItem[]): any[] {
  return perms
    .filter((p) => !p.meta?.hidden && !HIDDEN_MENU_PATHS.has(p.path || ""))
    .sort((a, b) => (a.meta?.sort ?? 0) - (b.meta?.sort ?? 0))
    .map((p) => ({
      path: p.path || "/",
      name: p.name,
      icon: getMenuIcon(p.meta?.icon),
      component: p.component,
      children: p.children?.length ? permissionsToMenuData(p.children as API.RouteMenuItem[]) : undefined,
    }));
}

/** 递归收集路由树中所有 path，用于去重 */
function collectAllPaths(routes: any[]): Set<string> {
  const paths = new Set<string>();
  for (const r of routes) {
    if (r.path) paths.add(r.path);
    if (r.children?.length) {
      for (const p of collectAllPaths(r.children)) {
        paths.add(p);
      }
    }
  }
  return paths;
}

/**
 * 根据权限树构建需要动态注入的路由。
 * 只对 component 在注册表中且 path 不在已有路由里的节点生效。
 */
function buildDynamicRoutes(perms: API.RouteMenuItem[], existingPaths: Set<string>): any[] {
  const routes: any[] = [];
  for (const p of perms) {
    if (!p.path) continue;

    if (!existingPaths.has(p.path) && p.component) {
      const LazyComp = getPageComponent(p.component);
      routes.push({
        path: p.path,
        name: p.name,
        element: React.createElement(Suspense, { fallback: null }, React.createElement(LazyComp)),
      });
      existingPaths.add(p.path); // 避免同路径重复注入
    }

    // 递归处理子菜单
    if (p.children?.length) {
      routes.push(...buildDynamicRoutes(p.children, existingPaths));
    }
  }
  return routes;
}

// ─────────────────────────────────────────────
// UmiJS 运行时 Hooks
// ─────────────────────────────────────────────

/**
 * render 钩子：React 首次渲染前执行。
 * 拉取当前用户的可见菜单树，缓存到模块变量，
 * 供 patchClientRoutes（路由注入）和 layout（菜单渲染）使用。
 */
export function render(oldRender: () => void) {
  if (window.location.pathname === loginPath) {
    oldRender();
    return;
  }

  const token = getAuthToken();
  if (!token) {
    oldRender();
    return;
  }

  fetch("/api/sys/permission/routes", {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (data?.success && Array.isArray(data.data) && data.data.length > 0) {
        permissionMenus = data.data;
      }
    })
    .catch(() => {
      // 接口不存在或出错时降级到静态路由，不阻塞渲染
    })
    .finally(() => oldRender());
}

/**
 * patchClientRoutes：在路由树注册后、首次渲染前修改路由。
 * 将后端菜单树中有组件映射的路由动态注入到 layout 下。
 */
export function patchClientRoutes({ routes }: { routes: any[] }) {
  if (permissionMenus.length === 0) return;

  const existingPaths = collectAllPaths(routes);
  const newRoutes = buildDynamicRoutes(permissionMenus, existingPaths);
  if (newRoutes.length === 0) return;

  // 找到 layout 包装路由（特征：children 里有 /dashboard）
  function injectIntoLayout(rs: any[]): boolean {
    for (const r of rs) {
      if (!Array.isArray(r.children)) continue;
      if (r.children.some((c: any) => c.path === "/dashboard")) {
        r.children.push(...newRoutes);
        return true;
      }
      if (injectIntoLayout(r.children)) return true;
    }
    return false;
  }

  if (!injectIntoLayout(routes)) {
    // 降级：在 catch-all (*) 前插入顶层
    const catchIdx = routes.findIndex((r: any) => r.path === "*");
    routes.splice(catchIdx > -1 ? catchIdx : routes.length, 0, ...newRoutes);
  }
}

function loginDataToUser(data: API.LoginData): API.CurrentUser {
  return {
    id: data.userId ? Number(data.userId) : undefined,
    userId: data.userId,
    username: data.username,
    realName: data.realName,
    avatar: data.avatar,
    roles: data.roles || [],
    permissions: data.permissions || [],
    isSuperAdmin: (data.roles || []).includes("SUPER_ADMIN") || (data.roles || []).includes("ACCOUNT_MANAGER"),
    teamId: data.teamId,
    teamName: data.teamName,
    agencyNames: data.agencyNames || [],
    groupNames: data.groupNames || [],
  };
}

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const res = await getUserInfo();
      if (res.success && res.data) {
        appVersion = res.data.version || appVersion;
        return loginDataToUser(res.data);
      }
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

const LatestUnreadChangelogModal: React.FC<{ currentUser?: API.CurrentUser }> = ({ currentUser }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [record, setRecord] = useState<API.SysChangelog | undefined>(undefined);
  const loadedUserRef = useRef<string | number | undefined>(undefined);

  useEffect(() => {
    const userKey = currentUser?.userId || currentUser?.id;
    if (!userKey || loadedUserRef.current === userKey) {
      return;
    }
    loadedUserRef.current = userKey;
    setLoading(true);
    getLatestUnreadChangelog()
      .then((res) => {
        if (res?.success && res.data?.id) {
          setRecord(res.data);
          setOpen(true);
        } else {
          setRecord(undefined);
          setOpen(false);
        }
      })
      .finally(() => setLoading(false));
  }, [currentUser?.id, currentUser?.userId]);

  const handleRead = async (goToList?: boolean) => {
    if (!record?.id) {
      setOpen(false);
      return;
    }
    setSubmitting(true);
    try {
      const res = await markChangelogRead(record.id);
      if (!res?.success) {
        message.error(res?.errorMessage || "更新日志已读状态提交失败");
        return;
      }
      setOpen(false);
      setRecord(undefined);
      if (goToList) {
        history.push("/changelog");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser || loading || !record) {
    return null;
  }

  return (
    <Modal
      title="最新更新日志"
      open={open}
      width={860}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={
        <Space>
          <Button onClick={() => handleRead(false)} loading={submitting}>
            我知道了
          </Button>
          <Button type="primary" onClick={() => handleRead(true)} loading={submitting}>
            查看全部
          </Button>
        </Space>
      }
    >
      <div style={{ marginBottom: 14 }}>
        <Space size={8} wrap>
          <Tag color="blue" style={{ borderRadius: 999, fontWeight: 600 }}>
            {record.version}
          </Tag>
          <Tag color="red" style={{ borderRadius: 999 }}>
            未读
          </Tag>
          <Title level={4} style={{ margin: 0, fontSize: 16 }}>
            {record.title}
          </Title>
        </Space>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.publishTime ? dayjs(record.publishTime).format("YYYY-MM-DD HH:mm") : dayjs(record.createTime).format("YYYY-MM-DD HH:mm")}
          </Text>
        </div>
      </div>
      <div
        style={{
          maxHeight: "60vh",
          overflowY: "auto",
          padding: "16px 18px",
          border: "1px solid #eef2f7",
          borderRadius: 12,
          background: "#fff",
        }}
      >
        <MarkdownRenderer content={record.content} />
      </div>
    </Modal>
  );
};

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [],
    headerContentRender: () => (
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: 13,
            lineHeight: "22px",
            fontWeight: 500,
            color: "#334155",
            letterSpacing: "0.2px",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span style={{ color: "#64748b", fontWeight: 400 }}>当前版本：</span>
          <span>v{appVersion}</span>
        </span>
      </div>
    ),
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.realName,
    // },
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },

    /**
     * 动态菜单渲染：当后端返回了菜单树时，用权限树替换默认的路由菜单。
     * 菜单项的 path 需与 config/routes.ts 中的路由路径对应才能正确导航。
     * 若后端没有返回菜单数据则降级到静态路由菜单。
     */
    menuDataRender: permissionMenus.length > 0 ? () => permissionsToMenuData(permissionMenus) : undefined,

    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    menuHeaderRender: undefined,
    childrenRender: (children) => {
      return (
        <>
          {children}
          <LatestUnreadChangelogModal currentUser={initialState?.currentUser} />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  baseURL: "/",
  paramsSerializer: (params: Record<string, unknown>) => {
    const parts: string[] = [];
    for (const [key, val] of Object.entries(params)) {
      if (val == null) continue;
      if (Array.isArray(val)) {
        val.forEach((v) => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`));
      } else {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`);
      }
    }
    return parts.join("&");
  },
  ...errorConfig,
};
